// ColumnsMenu – portal popover to show/hide master and live columns (matches table header labels).

import React, { JSX, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Columns3 } from 'lucide-react';
import type { ExtraColumnKey } from '../../../../types/DeviceListTypes';
import { extraColumns } from '../../../../types/DeviceListTypes';
import { BTN_ICON } from '../utils/deviceListConstants';
import { EXTRA_COLUMNS_HIDDEN_FROM_PICKER, extraColumnHeaderLabel } from '../utils/deviceTableExtraLabels';

const MENU_MIN_WIDTH = 288;
const MENU_MAX_WIDTH = 360;

import type { ColumnsMenuProps } from '../types/DeviceListViewProps';

export type { ColumnsMenuProps };

function CheckboxRow(props: {
  checked: boolean;
  onToggle: () => void;
  label: string;
  mono?: boolean;
}): JSX.Element {
  const { checked, onToggle, label, mono } = props;
  return (
    <label className="flex cursor-pointer items-start gap-2.5 rounded-lg px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-slate-800">
      <input
        type="checkbox"
        className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500/30"
        checked={checked}
        onChange={onToggle}
      />
      <span
        className={[
          'min-w-0 flex-1 text-xs leading-snug text-gray-800 dark:text-slate-200',
          mono ? 'font-mono text-[11px] text-gray-700 dark:text-slate-300' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {label}
      </span>
    </label>
  );
}

function SectionTitle(props: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 first:pt-0">
      {props.children}
    </div>
  );
}

export function ColumnsMenu(props: ColumnsMenuProps): JSX.Element | null {
  const {
    columns,
    visibleMasterColumns,
    visibleExtraColumns,
    onToggleMasterColumn,
    onToggleExtraColumn,
    open,
    onClose,
    anchorRef,
  } = props;

  const [coords, setCoords] = useState({ top: 0, left: 0, width: MENU_MIN_WIDTH });

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useLayoutEffect(() => {
    if (!open) return;

    function updatePosition(): void {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const width = Math.min(
        MENU_MAX_WIDTH,
        Math.max(MENU_MIN_WIDTH, Math.min(r.width + 200, window.innerWidth - 24)),
      );
      let left = r.right - width;
      left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
      const top = r.bottom + 6;
      setCoords({ top, left, width });
    }

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, anchorRef]);

  const masterColumnsExcludingId = columns.slice(1);
  const pickerExtraColumns = extraColumns.filter(
    ({ key }) =>
      !EXTRA_COLUMNS_HIDDEN_FROM_PICKER.has(key)
      && key !== '__lifecycle_lost'
      && key !== '__lifecycle_decommissioned'
      && key !== '__lifecycle_damaged'
      && key !== '__lifecycle_errored'
      && key !== '__lifecycle_note',
  );

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[280] bg-slate-900/25 dark:bg-black/50" role="presentation" onClick={onClose} />
      <div
        className="fixed z-[290] flex max-h-[min(72vh,520px)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-left text-gray-800 shadow-2xl ring-1 ring-black/5 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        style={{ top: coords.top, left: coords.left, width: coords.width }}
        role="dialog"
        aria-modal="true"
        aria-label="Choose visible columns"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-3 py-2">
          <div className="text-xs font-semibold text-gray-800 dark:text-slate-200">Visible columns</div>
          <p className="mt-0.5 text-[11px] leading-snug text-gray-500 dark:text-slate-400">
            Matches the table below. Device ID is always shown.
          </p>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-1.5 py-2">
          <SectionTitle>Device master</SectionTitle>
          <div className="space-y-0.5">
            {masterColumnsExcludingId.map((col) => (
              <CheckboxRow
                key={col}
                checked={visibleMasterColumns.includes(col)}
                onToggle={() => onToggleMasterColumn(col)}
                label={col}
                mono
              />
            ))}
          </div>

          <div className="mx-2 my-2 border-t border-gray-100 dark:border-slate-700" />

          <SectionTitle>Live status & commands</SectionTitle>
          <div className="space-y-0.5">
            {pickerExtraColumns.map(({ key }) => (
              <CheckboxRow
                key={key}
                checked={visibleExtraColumns.includes(key)}
                onToggle={() => onToggleExtraColumn(key)}
                label={extraColumnHeaderLabel(key)}
              />
            ))}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

import type { DeviceTableColumnsTriggerProps } from '../types/DeviceListViewProps';

export type { DeviceTableColumnsTriggerProps };

/** Toolbar strip control aligned with table header (gray bar). */
export function DeviceTableColumnsTrigger(props: DeviceTableColumnsTriggerProps): JSX.Element {
  const { open, onToggle, buttonRef } = props;
  return (
    <button
      ref={buttonRef}
      type="button"
      className={BTN_ICON}
      aria-expanded={open}
      aria-haspopup="dialog"
      aria-label="Columns"
      title="Columns"
      onClick={onToggle}
    >
      <Columns3 className="h-4 w-4 opacity-80" aria-hidden />
    </button>
  );
}
